import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { EXPENSE_CATEGORIES, PAYMENT_METHODS } from "@/lib/finance";

const empty = {
  label: "",
  category: "autre",
  amount: "",
  currency: "USD",
  date: new Date().toISOString().slice(0, 10),
  payment_method: "espece",
  receipt_url: "",
  note: "",
};

export default function ExpenseDrawer({ open, onClose, dealId, expense, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (expense) setForm({ ...empty, ...expense });
    else setForm(empty);
  }, [expense, open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.label.trim()) return toast.error("Le libellé est requis");
    const amount = Number(form.amount);
    if (!amount || amount <= 0) return toast.error("Montant invalide");
    setSaving(true);
    try {
      const payload = { ...form, amount };
      if (expense?.id) {
        await api.put(`/deals/${dealId}/expenses/${expense.id}`, payload);
        toast.success("Dépense mise à jour");
      } else {
        await api.post(`/deals/${dealId}/expenses`, payload);
        toast.success("Dépense enregistrée");
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />
      <aside
        className="fixed inset-y-0 right-0 z-50 w-full max-w-[520px] bg-white shadow-2xl flex flex-col"
        data-testid="expense-drawer"
      >
        <header className="h-14 px-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-brand-orange font-bold">Dépense</div>
            <h3 className="font-display text-lg text-brand-navy">
              {expense ? "Modifier la dépense" : "Ajouter une dépense"}
            </h3>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center" aria-label="Fermer">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            <Label>Libellé *</Label>
            <Input
              value={form.label}
              onChange={(e) => update("label", e.target.value)}
              placeholder="Ex : Séance photo studio"
              data-testid="exp-label"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => update("category", v)}>
                <SelectTrigger data-testid="exp-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EXPENSE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={form.date} onChange={(e) => update("date", e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label>Montant *</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => update("amount", e.target.value)} data-testid="exp-amount" />
            </div>
            <div>
              <Label>Devise</Label>
              <Select value={form.currency} onValueChange={(v) => update("currency", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="CDF">CDF</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Moyen de paiement</Label>
            <Select value={form.payment_method} onValueChange={(v) => update("payment_method", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Justificatif (URL / lien)</Label>
            <Input value={form.receipt_url} onChange={(e) => update("receipt_url", e.target.value)} placeholder="Lien vers scan / reçu" />
          </div>

          <div>
            <Label>Note</Label>
            <Textarea rows={3} value={form.note} onChange={(e) => update("note", e.target.value)} />
          </div>
        </div>

        <footer className="p-5 border-t border-slate-200 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-brand-orange hover:bg-brand-orangeDark text-white" data-testid="exp-save">
            {saving ? "Enregistrement…" : "Enregistrer la dépense"}
          </Button>
        </footer>
      </aside>
    </>
  );
}
